import React, { forwardRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, IconButton, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Label,
  NumericTextField,
  ReadOnly,
  TextField,
  useLocalization,
} from '@ui-framework/index';
import IconLibrary from '../../../IconLibrary';
import Currency from '../../../Currency';

function getHeaders(formatMessage, unUsedFields) {
  return {
    action: {
      key: 'action',
      label: '',
      required: false,
    },
    partLineCode: {
      key: 'partLineCode',
      label: formatMessage({
        id: 'LINE_CODE',
        defaultMessage: 'Line Code',
      }),
      required: false,
    },
    partNumber: {
      key: 'partNumber',
      label: formatMessage({
        id: 'PART_NUMBER',
        defaultMessage: 'Part Number',
      }),
      required: true,
    },
    partDescription: {
      key: 'partDescription',
      label: formatMessage({
        id: 'DESCRIPTION',
        defaultMessage: 'Description',
      }),
      required: false,
    },
    partQuantity: {
      key: 'partQuantity',
      label: formatMessage({
        id: 'QUANTITY',
        defaultMessage: 'Quantity',
      }),
      required: true,
    },
    partUnitCost: {
      key: 'partUnitCost',
      label: formatMessage({
        id: 'UNIT_COST',
        defaultMessage: 'Unit Cost',
      }),
      required: true,
    },
    // unused fields must match with ClaimPartAmountTypeEnum
    ...(!unUsedFields.includes('HANDLING')
      ? {
          partHandling: {
            key: 'partHandling',
            label: formatMessage({
              id: 'HANDLING',
              defaultMessage: 'Handling',
            }),
            required: false,
          },
        }
      : {}),
    ...(!unUsedFields.includes('TAX')
      ? {
          partTax: {
            key: 'partTax',
            label: formatMessage({
              id: 'TAX',
              defaultMessage: 'Tax',
            }),
            required: false,
          },
        }
      : {}),
    total: {
      key: 'total',
      label: formatMessage({
        id: 'TOTAL',
        defaultMessage: 'Total',
      }),
      required: false,
    },
  };
}

const emptyPartRow = {
  partLineCode: '',
  partNumber: '',
  description: '',
  partQuantity: '',
  partUnitCost: '',
  partHandling: '',
  partTax: '',
};

const deleteUnusedFields = (fieldObj, unUsedFields) => {
  const newObj = { ...fieldObj };
  unUsedFields.forEach(field => {
    switch (field) {
      case 'HANDLING':
        delete newObj.partHandling;
        break;
      case 'TAX':
        delete newObj.partTax;
        break;
      default:
        break;
    }
  });
  return newObj;
};

function PartLabel({ idx, children, ...props }) {
  if (idx > 0) {
    return null;
  }
  return (
    <Label {...props} style={{ display: 'block', marginBottom: '5px' }}>
      {children}
    </Label>
  );
}

const ClaimParts = forwardRef((props, ref) => {
  const { formatMessage } = useLocalization();
  const { control, watch } = useFormContext();
  const [currentRows, setCurrentRows] = useState([]);

  const unUsedFields = props?.unusedFields || [];
  const maxFields = props?.field?.max || 100;
  const headers = getHeaders(formatMessage, unUsedFields);

  const { fields, append, remove } = useFieldArray({
    control,
    name: props?.field?.name,
  });

  const parts = watch(props?.field?.name || 'undefined');

  useEffect(() => {
    if (Array.isArray(parts) && parts.length > 0) {
      setCurrentRows([...parts]);
    }
  }, [parts]);

  const partUpdateOrDeleteAllowed =
    props?.permissions?.fields?.claimParts?.quantity === 'WRITE';

  return (
    <Box ref={ref} sx={{}}>
      {fields.map((field, idx) => {
        const { partQuantity, partUnitCost, partHandling, partTax } =
          currentRows[idx] || {
            partQuantity: 0,
            partUnitCost: 0,
            partHandling: 0,
            partTax: 0,
          };
        const q = parseFloat(partQuantity) || 0;
        const u = parseFloat(partUnitCost) || 0;
        const h = parseFloat(partHandling) || 0;
        const t = parseFloat(partTax) || 0;
        let total = 0;
        if (q > 0) {
          total = parseFloat(q * u + h + t).toFixed(2) || 0;
        }

        return (
          <Grid2 container spacing={1} key={field.id} sx={{ mb: 1 }}>
            {partUpdateOrDeleteAllowed && (
              <Grid2 xs={0.5}>
                <PartLabel idx={idx} component="div">
                  &nbsp;
                </PartLabel>
                <IconButton
                  size="small"
                  onClick={() => {
                    remove(idx);
                  }}
                  color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid2>
            )}
            <Grid2 xs={12} md={1}>
              <PartLabel idx={idx} required={headers?.partLineCode?.required}>
                {headers.partLineCode?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partLineCode`}
                render={({ field: controllerField }) => (
                  <ReadOnly>{controllerField?.value}&nbsp;</ReadOnly>
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={2}>
              <PartLabel idx={idx} required={headers?.partNumber?.required}>
                {headers.partNumber?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partNumber`}
                render={({
                  field: controllerField,
                  fieldState: { error: fsError },
                }) => (
                  <TextField
                    {...controllerField}
                    error={fsError?.message || false}
                    helperText={fsError?.message}
                    value={
                      controllerField.value === undefined
                        ? ''
                        : controllerField.value
                    }
                    permission={
                      props?.permissions?.fields?.claimParts?.partNumber
                    }
                  />
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={2}>
              <PartLabel
                idx={idx}
                required={headers?.partDescription?.required}>
                {headers.partDescription?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partDescription`}
                render={({ field: controllerField }) => (
                  <TextField
                    {...controllerField}
                    value={
                      controllerField.value === undefined
                        ? ''
                        : controllerField.value
                    }
                    permission={
                      props?.permissions?.fields?.claimParts?.partDescription
                    }
                  />
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={1}>
              <PartLabel idx={idx} required={headers?.partQuantity?.required}>
                {headers.partQuantity?.label}
              </PartLabel>
              <Controller
                control={control}
                name={`${props.field.name}.${idx}.partQuantity`}
                render={({
                  field: controllerField,
                  fieldState: { error: fsError },
                }) => (
                  <NumericTextField
                    {...controllerField}
                    error={fsError?.message || false}
                    helperText={fsError?.message}
                    value={
                      controllerField.value === undefined
                        ? ''
                        : controllerField.value
                    }
                    permission={
                      props?.permissions?.fields?.claimParts?.quantity
                    }
                    positiveNumber
                    decimalScale={0}
                  />
                )}
              />
            </Grid2>
            <Grid2 xs={12} md={1}>
              <PartLabel idx={idx} required={headers?.partUnitCost?.required}>
                {headers.partUnitCost?.label}
              </PartLabel>
              <NumericTextField
                decimalScale={2}
                isCurrency
                fixedDecimalScale
                positiveNumber
                startAdornment="$"
                name={`${props.field.name}.${idx}.partUnitCost`}
                permission={props?.permissions?.fields?.claimParts?.partCost}
              />
            </Grid2>
            {headers.partHandling && (
              <Grid2 xs={12} md={1}>
                <PartLabel idx={idx} required={headers?.partHandling?.required}>
                  {headers.partHandling?.label}
                </PartLabel>
                <NumericTextField
                  decimalScale={2}
                  isCurrency
                  fixedDecimalScale
                  startAdornment="$"
                  positiveNumber
                  name={`${props.field.name}.${idx}.partHandling`}
                  permission={
                    props?.permissions?.fields?.claimParts?.amounts
                      ?.amountRequested
                  }
                />
              </Grid2>
            )}
            {headers.partTax && (
              <Grid2 xs={12} md={1}>
                <PartLabel idx={idx} required={headers?.partTax?.required}>
                  {headers.partTax?.label}
                </PartLabel>
                <NumericTextField
                  decimalScale={2}
                  isCurrency
                  fixedDecimalScale
                  startAdornment="$"
                  positiveNumber
                  name={`${props.field.name}.${idx}.partTax`}
                  permission={
                    props?.permissions?.fields?.claimParts?.amounts
                      ?.amountRequested
                  }
                />
              </Grid2>
            )}

            <Grid2 xs={12} md={1}>
              <PartLabel idx={idx} required={headers?.total?.required}>
                {headers.total?.label}
              </PartLabel>
              <ReadOnly>
                <Currency>{total}</Currency>
              </ReadOnly>
            </Grid2>
          </Grid2>
        );
      })}
      {(fields.length < maxFields || fields.length === 0) &&
        partUpdateOrDeleteAllowed && (
          <Button
            variant="text"
            startIcon={<IconLibrary.Add fontSize="inherit" />}
            onClick={() => {
              // Delete unused fields from empty part row
              const newPartRow = deleteUnusedFields(emptyPartRow, unUsedFields);
              append({ ...newPartRow });
            }}
            sx={{ mt: 0 }}>
            {formatMessage({
              id: 'SERVICE_JOB_ADD_NEW_PART_BUTTON',
              defaultMessage: 'Add New Part',
            })}
          </Button>
        )}
    </Box>
  );
});

export default observer(ClaimParts);
